import { Suspense, useEffect } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { TOOL_BY_ID, categoryOf } from "@/lib/toolkit/registry";
import { pushRecentTool } from "@/lib/toolkit/storage";
import { ToolSkeleton } from "@/components/toolkit/tool-skeleton";
import { PremiumGate } from "@/components/billing/premium-lock";

export const Route = createFileRoute("/toolkit/$toolId")({
  loader: ({ params }) => {
    const tool = TOOL_BY_ID.get(params.toolId);
    if (!tool) throw notFound();
    return { name: tool.name, description: tool.description, category: tool.category };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Tool unavailable" }, { name: "robots", content: "noindex" }] };
    }
    return {
      ...seo({
        title: `${loaderData.name} — JEE Toolkit | The Quantum Minds`,
        description: loaderData.description,
        path: `/toolkit/${params.toolId}`,
      }),
      scripts: [
        breadcrumbScript([
          { name: "Home", path: "/" },
          { name: "JEE Toolkit", path: "/toolkit" },
          { name: loaderData.name, path: `/toolkit/${params.toolId}` },
        ]),
      ],
    };
  },
  component: ToolPage,
  notFoundComponent: ToolNotFound,
});

function ToolPage() {
  const { toolId } = Route.useParams();
  const data = Route.useLoaderData();
  const tool = TOOL_BY_ID.get(toolId)!;
  const Component = tool.component;
  const category = categoryOf(data.category);

  useEffect(() => {
    pushRecentTool({ id: tool.id, name: tool.name, category: tool.category });
  }, [tool.id, tool.name, tool.category]);

  return (
    <main className="page-shell min-w-0 overflow-x-clip px-5 lg:px-8 pt-8 [box-sizing:border-box]">
      <Link
        to="/toolkit/c/$categoryId"
        params={{ categoryId: data.category }}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors duration-200 hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" /> {category?.name ?? "Toolkit"}
      </Link>

      <header className="mt-3 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-primary/12 text-primary">
          <tool.icon className="size-5" />
        </span>
        <div className="min-w-0">
          <h1 className="break-words text-2xl font-bold leading-tight tracking-tight">{data.name}</h1>
          <p className="mt-0.5 break-words text-xs text-muted-foreground">{data.description}</p>
        </div>
      </header>

      <div className="mt-5 w-full min-w-0 max-w-full pb-8">
        <PremiumGate>
          <Suspense fallback={<ToolSkeleton />}>
            <Component />
          </Suspense>
        </PremiumGate>
      </div>
    </main>
  );
}

function ToolNotFound() {
  return (
    <main className="page-shell min-w-0 overflow-x-clip px-5 lg:px-8 pt-16 text-center [box-sizing:border-box]">
      <h1 className="text-xl font-bold">We couldn&apos;t find that tool</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        It may have been renamed. Browse the toolkit to find it again.
      </p>
      <Link to="/toolkit" className="mt-4 inline-block text-sm font-semibold text-primary">
        Back to the toolkit
      </Link>
    </main>
  );
}
